<template>
    <div v-if="clientId" class="google-login-wrap" :style="{ width: width + 'px' }">
        <!-- Sichtbarer Button im Quaro-Look (Pill, outlined). Rein dekorativ: den Klick nimmt der
             unsichtbare Google-Button darueber entgegen, der auch den zugaenglichen Namen traegt. -->
        <v-btn outlined rounded block class="google-login-btn" :color="color" tabindex="-1" aria-hidden="true" type="button">
            <span class="google-login-logo" aria-hidden="true" v-html="googleLogoSvg"></span>
            <span class="google-login-label">{{ label }}</span>
        </v-btn>

        <!-- Von Google per JS-API (renderButton) in diesen Container gerendert - bei JEDEM Mount, nicht
             nur beim ersten Seitenladen (das HTML-Attribut-Setup mit g_id_onload wurde vom Google-Script
             nur einmal gescannt; nach Logout -> /login blieb der Platzhalter leer und der Klick lief ins
             Leere). Optisch nicht anpassbar, deshalb mit opacity 0 exakt ueber dem sichtbaren Button. -->
        <div ref="native" class="google-login-native"></div>
    </div>
</template>
<script>
    import auth from "../components/helper/auth"

    // Offizielles Google-"G" (Branding-Richtlinien: Logo unveraendert, auf weissem Grund).
    const GOOGLE_LOGO_SVG =
        '<svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">' +
        '<path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>' +
        '<path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>' +
        '<path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>' +
        '<path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>' +
        "</svg>"

    // Das GIS-Script genau einmal pro Seite laden; weitere Mounts (Login -> Register -> Login, oder
    // Logout -> Login) warten auf dasselbe Promise, statt das Script erneut einzuhaengen.
    function loadGoogleIdentityScript() {
        if (window.google && window.google.accounts && window.google.accounts.id) return Promise.resolve()
        if (!window.__quaroGisLoading) {
            window.__quaroGisLoading = new Promise((resolve, reject) => {
                const script = document.createElement("script")
                script.src = "https://accounts.google.com/gsi/client"
                script.async = true
                script.defer = true
                script.onload = () => resolve()
                script.onerror = (error) => {
                    delete window.__quaroGisLoading
                    reject(error)
                }
                document.head.appendChild(script)
            })
        }
        return window.__quaroGisLoading
    }

    export default {
        name: "GoogleLogin",
        props: {
            // Google-seitiger Text des (unsichtbaren) Buttons: "signin_with" | "signup_with" - nur fuer
            // Screenreader relevant, sichtbar ist "label".
            text: { type: String, default: "signin_with" },
            label: { type: String, default: "Sign in with Google" },
            // Breite in px (Google erlaubt 200-400)
            width: { type: [Number, String], default: 260 },
            color: { type: String, default: "primary" }
        },
        data() {
            return {
                clientId: process.env.VUE_APP_GOOGLE_CLIENT_ID || "",
                googleLogoSvg: GOOGLE_LOGO_SVG
            }
        },
        mounted() {
            if (!this.clientId) return
            loadGoogleIdentityScript()
                .then(() => {
                    if (!this._isDestroyed) this.renderGoogleButton()
                })
                .catch((error) => {
                    console.log("Google Identity Services konnte nicht geladen werden", error)
                })
        },
        methods: {
            // Explizit per JS-API rendern, damit der Button auch nach SPA-Navigation (Logout -> Login)
            // wieder da ist. initialize() darf mehrfach aufgerufen werden und bindet den Callback
            // jeweils an die aktuelle Komponenteninstanz.
            renderGoogleButton() {
                const gis = window.google && window.google.accounts && window.google.accounts.id
                const el = this.$refs.native
                if (!gis || !el) return
                gis.initialize({
                    client_id: this.clientId,
                    callback: this.handleCredentialResponse,
                    ux_mode: "popup",
                    auto_select: false,
                    cancel_on_tap_outside: true
                })
                el.innerHTML = ""
                gis.renderButton(el, {
                    type: "standard",
                    shape: "pill",
                    theme: "outline",
                    text: this.text,
                    size: "large",
                    logo_alignment: "left",
                    width: Number(this.width)
                })
            },
            // Das ID-Token geht unveraendert ans Backend (searchinsights-api /user/login/oauth), das es
            // serverseitig gegen Google verifiziert - hier wird bewusst nichts mehr aus dem JWT gelesen
            // oder vertraut (das war vorher der Fall und liess sich clientseitig faelschen).
            handleCredentialResponse(response) {
                try {
                    this.$matomo.trackEvent("Login", "google-signin", "", "")
                } catch (error) {}
                auth.oauthLogin("google", response.credential, (result) => {
                    if (result.authentication) {
                        this.$emit("login-success", result.mail)
                    } else {
                        this.$emit("login-error", { oauth_error: result.oauth_error, singleusererror: result.singleusererror })
                    }
                })
            }
        }
    }
</script>
<style scoped>
    .google-login-wrap {
        position: relative;
        height: 40px;
        margin: 0 auto;
    }
    .google-login-btn {
        height: 40px !important;
        text-transform: none;
        letter-spacing: 0.2px;
        font-weight: 500;
        background-color: #ffffff;
    }
    .google-login-wrap:hover .google-login-btn {
        background-color: #f7faf9;
    }
    .google-login-logo {
        display: inline-flex;
        align-items: center;
        margin-right: 10px;
    }
    .google-login-label {
        line-height: 1;
    }
    /* Unsichtbarer Google-Button exakt ueber dem sichtbaren, faengt den Klick */
    .google-login-native {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 40px;
        opacity: 0;
        overflow: hidden;
        z-index: 1;
    }
</style>
